// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ModalEl} from 'common/components/modal';
import {SharingBannerEl} from 'common/widgets/sharing/banner';
import {InputEl} from 'common/components/input';
import {ButtonEl} from 'common/components/button';
// eslint-disable-next-line import/no-cycle
import {QueryEntrySectionEl} from 'common/query-builder/query-entry-section/panel';
import {RowLabelType} from 'common/query-builder/query-entry-section/types';
import {ButtonTheme, ButtonType} from 'common/components/button/constants';
import {Placement} from 'common/components/tooltip/types';
import {IncludeCustomPropertyType} from 'common/query-builder/query-block/properties-menu/types';

import type {CustomEventModalInternal} from './index';

export function template(this: CustomEventModalInternal) {
  const {error, open, stagedCustomEvent} = this.state;

  const canSaveCustomEvent = this.helpers.isStagedCustomEventValid();
  const nameHasError = error.invalidName || error.duplicateName || error.invalidSeparator;

  let nameErrorMessage = ``;

  if (nameHasError) {
    if (error.invalidName) {
      nameErrorMessage = this.t(`custom-event.errors.no-name`);
    } else if (error.duplicateName) {
      nameErrorMessage = this.t(`custom-event.errors.name-exists`);
    } else if (error.invalidSeparator) {
      nameErrorMessage = this.t(`custom-event.errors.invalid-character`);
    }
  }

  return (
    <ModalEl
      sel='.custom-event-modal'
      attrs={{
        open,
        closeable: true,
        'grow-to-content': true,
        'close-confirm-text': this.helpers.hasUnsavedChanges() ? this.t(`custom-event.errors.confirm-unsaved`) : ``,
      }}
      on={{
        change: (ev) => this.helpers.handleModalChange(ev),
      }}
    >
      <div sel='.custom-event-modal-content'>
        <div sel='.mp-modal-header-container'>
          <div sel='.mp-modal-header'>
            <div sel='.mp-modal-title'>
              {this.helpers.isNewCustomEvent()
                ? this.t(`custom-event.header.create`)
                : this.t(`custom-event.header.edit`, {eventName: stagedCustomEvent.name})}
            </div>
          </div>
        </div>
        <SharingBannerEl sel='.sharing-banner' attrs={this.helpers.getSharingBannerAttrs()} />
        <div sel='.custom-event-name-subheader'>
          <InputEl
            attrs={{
              maxlength: this.helpers.MAX_NAME_LENGTH,
              label: this.t(`custom-event.name`),
              necessary: `required`,
              'validation-status': nameHasError ? `invalid` : ``,
              'error-message': nameErrorMessage,
            }}
            on={{
              input: this.helpers.updateCustomEventName,
              focus: this.helpers.clearNameErrors,
            }}
            hook={{
              insert: this.helpers.nameInputInserted,
            }}
            props={{
              value: stagedCustomEvent.name,
            }}
          />
        </div>
        <div sel='.mp-modal-content'>
          <div sel='.custom-event-match-text'>{this.t(`custom-event.match-description`)}</div>
          <div sel='.custom-event-builder'>
            <QueryEntrySectionEl
              showAllEvents={this.canUseAllEvents()}
              dataGroupsEnabled={this.isDataGroupsEnabled()}
              dataGroupIds={this.params.dataGroupIds}
              disableFirstTimeEventFilters
              entries={this.getEntries()}
              enableAllDataGroupsForScdProps
              enableEnhancedListFilters
              hideEditControls
              includeCustomProperties={IncludeCustomPropertyType.PersistedNonBehavioralOnly}
              showBorrowedProperties
              projectHasPeople={this.attr(`project-has-people`)}
              projectId={this.attr(`project-id`)}
              rowDraggable
              rowLabelType={RowLabelType.None}
              workspaceId={this.attr(`workspace-id`)}
              projectAsOfDate={this.attr(`project-as-of-date`)}
              trackName={this.attr(`track-name`)}
              onUpdate={this.helpers.updateQueryEvents}
              onTrack={this.helpers.captureTracking}
            />
          </div>
        </div>
        <div sel='.footer'>
          <div sel='.delete-custom-event-button'>
            {stagedCustomEvent.id ? (
              <ButtonEl
                sel='.delete-custom-event-button'
                key='delete-custom-event-button'
                attrs={{
                  elref: `deleteCustomEventButton`,
                  type: ButtonType.Destructive,
                  icon: `trashcan`,
                  tooltip: {
                    placement: Placement.Right,
                    text: this.t(`custom-event.delete-tooltip`),
                    'always-show': true,
                  },
                }}
                on={{click: () => this.handleDeleteClick()}}
              />
            ) : this.helpers.showShareManagement() ? (
              <ButtonEl
                sel='.save-and-share-button'
                attrs={{
                  theme: ButtonTheme.Secondary,
                  disabled: !canSaveCustomEvent,
                }}
                on={{click: () => this.helpers.clickSave({andShare: true})}}
              >
                {this.t(`custom-event.save-and-share`)}
              </ButtonEl>
            ) : null}
          </div>
          <ButtonEl
            sel='.save-custom-event-button'
            attrs={{
              theme: ButtonTheme.Primary,
              disabled: !canSaveCustomEvent,
            }}
            on={{click: () => this.helpers.clickSave()}}
          >
            {this.t(`common.save`)}
          </ButtonEl>
        </div>
      </div>
    </ModalEl>
  );
}
